import {useEffect, useEffectEvent} from "react";
import {useLocation} from "react-router-dom";
import {getConsent, subscribeConsent} from "consent/consentStore";
import {useUser} from "UserProvider";
import {reportRedditRouteChange, syncRedditPixelWithConsent} from "./analytics";
import {redditMatchKeysFor} from "./redditPixel";

/**
 * Keeps the Reddit pixel in step with consent, with the signed-in identity, and with the route (ui#834).
 *
 * A thin adapter by mandate (epic #26): it holds no rules. Which fields may identify a person is
 * {@link redditMatchKeysFor}'s decision, whether anything may load at all is
 * {@link syncRedditPixelWithConsent}'s, WHERE it may load is `redditPixelAllowedOn`'s
 * (kitecorp/transferify-ui#2008), and what a navigation reports — including whether an owed `SignUp` is
 * spent on this page (api#1240) — is {@link reportRedditRouteChange}'s. All of them live in React-free
 * modules.
 *
 * Mounted on the app SHELL rather than in `RootLayout` beside `useAnalyticsConsent`, because unlike the
 * Google tag this one needs two things only the shell has: the session (for the match key) and the router
 * (for route changes). It still survives every route change, which is the property that matters — a host
 * that unmounted would stop reporting halfway through a visit.
 */
export function useRedditPixel(): void {
	const [user] = useUser();
	const location = useLocation();

	const email = user.email;
	// The account's own uuid, NOT `user.id`, which is the Firebase Auth uid (api#1240). Lifted here as a
	// dependency of its own because it arrives on a LATER render than the address does: the session is
	// built from the Firebase identity first and the `/account` payload lands after it, so an effect
	// keyed on the address alone would never re-run when the id shows up, and the match key would be
	// missing for the whole visit.
	const accountId = user.accountId;

	useEffect(() => {
		const sync = () => {
			syncRedditPixelWithConsent(getConsent(), redditMatchKeysFor({email, accountId}));
		};
		// Returning visitor: apply whatever was already decided, then follow every later change. Re-runs
		// when either identifier changes, which is what attaches an account's keys to a pixel that a
		// consenting visitor started before signing in.
		sync();
		return subscribeConsent(sync);
	}, [email, accountId]);

	// Reads the latest identifiers without making them dependencies: a login must not be reported as a
	// page view, and adding them here is the only other way to see the current values.
	const reportRoute = useEffectEvent(() => {
		reportRedditRouteChange(getConsent(), redditMatchKeysFor({email, accountId}));
	});

	// Runs on mount too, and must: the load that started the pixel is also a page the visitor is on, and
	// whether it has already been counted is decided in `redditPixel`, not by a flag kept here.
	useEffect(() => {
		reportRoute();
	}, [location.pathname]);
}
